import { unit } from 'mathjs';
import { useQuery } from 'react-query';
import { MatchedOrganizationEntity } from 'v2/models';
import { getRootDomain } from 'v2/utils/formValidator';
import restFactory from 'v2/utils/restFactory';
import {
  CompanyDescriptions,
  CompanyDescriptionsResponse,
  CompanyDetails,
  buildCompanyDetailsPrompt,
} from 'v2/utils/userUtils';

interface getCompanyDetailsProps {
  email: string;
  onProviderSuccess: (organizationEntity?: MatchedOrganizationEntity) => void;
  onCompanySuccess: (companyDescriptions: CompanyDescriptions) => void;
  onExternalCompanySuccess: (companyDetails?: CompanyDetails) => void;
}

export default function useGetCompanyDetails({
  email,
  onProviderSuccess,
  onCompanySuccess,
  onExternalCompanySuccess,
}: getCompanyDetailsProps) {
  const { data: matchedOrg } = useQuery(
    ['Get match provider for sellers'],
    async () => {
      const matchedOrg = await restFactory.get<MatchedOrganizationEntity>(
        '/data-providers/match'
      );
      if (Object.keys(matchedOrg).length > 0) {
        return matchedOrg;
      }
    },
    {
      cacheTime: 0,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        onProviderSuccess(data);
      },
    }
  );

  const { data: externalCompany } = useQuery(
    ['Get company details for buyers from pdl'],
    async () => {
      const address = email.split('@').pop() as string;
      const rootDomain = getRootDomain(address);
      const externalCompany = await restFactory.get<CompanyDetails>(
        `/dj/api/v1/data_vendor/retrieve_company_info`,
        { domain: rootDomain }
      );
      if (Object.keys(externalCompany).length > 0) {
        return externalCompany;
      }
    },
    {
      cacheTime: 0,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        onExternalCompanySuccess(data);
      },
    }
  );

  const { data: companyDetails } = useQuery(
    ['Get company details for buyers from gpt'],
    async () => {
      const newDescriptions =
        await restFactory.get<CompanyDescriptionsResponse>(
          `/dj/api/v1/gpt/generate_company_descriptions`,
          {
            prompt: buildCompanyDetailsPrompt(
              externalCompany as CompanyDetails
            ),
          },
          undefined,
          { timeout: unit('30s').toNumber('ms') }
        );
      return newDescriptions.content;
    },
    {
      cacheTime: 0,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      enabled: Boolean(externalCompany),
      onSuccess: (data) => {
        onCompanySuccess(data);
      },
    }
  );

  return { matchedOrg, companyDetails, externalCompany };
}
